import { PRODUCT_NAME } from "@mailhand/core/brand";
import { INPUT_SOURCES, INPUT_TYPES, type InputSource, TEMPLATE_VARIABLES, type TemplateVariable } from "@mailhand/core/schemas";
import { INPUT_SOURCE_LABEL, INPUT_TYPE_LABEL } from "../../app/labels.js";
import { Button, Field, Icon, Input, Select, Textarea } from "../../ui/index.js";
import { type FieldErrors, type InputDraft, type InputLock, isInputType, withSource } from "./draft.js";

const MAX_INPUTS = 10;

const TEMPLATE_VARIABLE_NAMES = Object.keys(TEMPLATE_VARIABLES).filter((name): name is TemplateVariable => Object.hasOwn(TEMPLATE_VARIABLES, name));

function isInputSource(value: string): value is InputSource {
  return INPUT_SOURCES.some((source) => source === value);
}

interface LockRule {
  note: string;
  sources: readonly InputSource[];
  removable: boolean;
  requiredFixed: boolean;
}

/** The forward `to` row needs a value the router cannot change. */
const LOCK_RULES: Record<Exclude<InputLock, null>, LockRule> = {
  "reply-text": { note: `The reply itself. ${PRODUCT_NAME} writes it from your instructions.`, sources: ["compose"], removable: false, requiredFixed: true },
  "forward-to": { note: "Where the message is forwarded.", sources: ["constant", "template"], removable: false, requiredFixed: true },
  "forward-summary": { note: "One line above the forwarded message.", sources: ["compose"], removable: true, requiredFixed: false },
};

function allowedSources(input: InputDraft): readonly InputSource[] {
  return input.lock === null ? INPUT_SOURCES : LOCK_RULES[input.lock].sources;
}

function descriptionHint(input: InputDraft): string | undefined {
  if (input.lock === "reply-text") return "Guidance for this reply, on top of your instructions.";
  if (input.source === "extract") return "Tell the router what to look for.";
  return undefined;
}

function descriptionPlaceholder(input: InputDraft): string {
  if (input.lock === "reply-text") return "Always link to the returns page.";
  return input.source === "compose" ? "What to write" : "Optional";
}

interface InputsEditorProps {
  inputs: InputDraft[];
  errors: FieldErrors;
  onChange: (inputs: InputDraft[]) => void;
  /** Null for an action type whose input list is fixed. */
  onAdd: (() => void) | null;
}

export function InputsEditor({ inputs, errors, onChange, onAdd }: InputsEditorProps) {
  function update(index: number, next: InputDraft): void {
    onChange(inputs.map((input, position) => (position === index ? next : input)));
  }
  function remove(index: number): void {
    onChange(inputs.filter((_input, position) => position !== index));
  }

  return (
    <div className="inputs-editor">
      {errors.inputs === undefined ? null : (
        <div className="field-error" role="alert">
          {errors.inputs}
        </div>
      )}
      {inputs.length === 0 ? <p className="muted small">No inputs. The action gets the message and the decision.</p> : null}
      {inputs.map((input, index) => (
        <InputRow key={input.key} input={input} index={index} errors={errors} onChange={(next) => { update(index, next); }} onRemove={() => { remove(index); }} />
      ))}
      {onAdd !== null && inputs.length < MAX_INPUTS ? (
        <Button size="sm" variant="ghost" onClick={onAdd}>
          <Icon name="plus" size={14} /> Add an input
        </Button>
      ) : null}
    </div>
  );
}

interface InputRowProps {
  input: InputDraft;
  index: number;
  errors: FieldErrors;
  onChange: (input: InputDraft) => void;
  onRemove: () => void;
}

function InputRow({ input, index, errors, onChange, onRemove }: InputRowProps) {
  const rule = input.lock === null ? null : LOCK_RULES[input.lock];
  const locked = rule !== null;
  const typeFixed = locked || input.source === "compose";
  const errorAt = (field: string): string | undefined => errors[`inputs.${index}.${field}`];
  const label = input.name === "" ? `Input ${index + 1}` : input.name;

  return (
    <fieldset className="input-row" aria-label={label}>
      <div className="input-row-head">
        <span className="input-row-title mono">
          {locked ? <Icon name="lock" size={13} /> : null}
          {label}
        </span>
        <span className="button-row">
          {rule === null ? null : <span className="muted small">{rule.note}</span>}
          {rule === null || rule.removable ? (
            <button type="button" className="icon-button" onClick={onRemove} aria-label={`Remove ${label}`}>
              <Icon name="trash" size={15} />
            </button>
          ) : null}
        </span>
      </div>
      <div className="input-row-grid">
        <Field label="Name" error={errorAt("name")}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              className="mono"
              value={input.name}
              disabled={locked}
              placeholder="orderNumber"
              aria-describedby={describedBy}
              aria-invalid={invalid}
              onChange={(event) => { onChange({ ...input, name: event.target.value }); }}
            />
          )}
        </Field>
        <Field label="Source">
          {({ id }) => (
            <Select
              id={id}
              value={input.source}
              disabled={allowedSources(input).length === 1}
              onChange={(event) => {
                const value = event.target.value;
                if (isInputSource(value)) onChange(withSource(input, value));
              }}
            >
              {allowedSources(input).map((source) => (
                <option key={source} value={source}>
                  {INPUT_SOURCE_LABEL[source]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Type" hint={input.source === "compose" && !locked ? `${PRODUCT_NAME} writes long text.` : undefined}>
          {({ id, describedBy }) => (
            <Select
              id={id}
              value={input.type}
              disabled={typeFixed}
              aria-describedby={describedBy}
              onChange={(event) => {
                const value = event.target.value;
                if (isInputType(value)) onChange({ ...input, type: value });
              }}
            >
              {INPUT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {INPUT_TYPE_LABEL[type]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <label className="checkbox">
          <input type="checkbox" checked={input.required} disabled={rule?.requiredFixed ?? false} onChange={(event) => { onChange({ ...input, required: event.target.checked }); }} />
          <span>Required</span>
        </label>
      </div>
      {input.source === "constant" ? <ConstantValue input={input} error={errorAt("value")} onChange={onChange} /> : null}
      {input.source === "template" ? <TemplateValue input={input} error={errorAt("value")} onChange={onChange} /> : null}
      <Field label="Description" hint={descriptionHint(input)} error={errorAt("description")}>
        {({ id, describedBy, invalid }) => (
          <Input
            id={id}
            value={input.description}
            placeholder={descriptionPlaceholder(input)}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            onChange={(event) => { onChange({ ...input, description: event.target.value }); }}
          />
        )}
      </Field>
    </fieldset>
  );
}

interface ValueProps {
  input: InputDraft;
  error: string | undefined;
  onChange: (input: InputDraft) => void;
}

function ConstantValue({ input, error, onChange }: ValueProps) {
  return (
    <Field label={input.lock === "forward-to" ? "Forward to" : "Value"} error={error}>
      {({ id, describedBy, invalid }) => {
        const common = { id, "aria-describedby": describedBy, "aria-invalid": invalid };
        if (input.type === "boolean") {
          return (
            <Select {...common} value={input.value} onChange={(event) => { onChange({ ...input, value: event.target.value }); }}>
              <option value="">Choose</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Select>
          );
        }
        if (input.type === "text") return <Textarea {...common} rows={3} value={input.value} onChange={(event) => { onChange({ ...input, value: event.target.value }); }} />;
        return (
          <Input
            {...common}
            type={input.type === "email" ? "email" : input.type === "number" ? "number" : "text"}
            placeholder={input.type === "email" ? "team@yourbusiness.com" : undefined}
            value={input.value}
            onChange={(event) => { onChange({ ...input, value: event.target.value }); }}
          />
        );
      }}
    </Field>
  );
}

function TemplateValue({ input, error, onChange }: ValueProps) {
  const variables = input.type === "email" ? TEMPLATE_VARIABLE_NAMES.filter((name) => TEMPLATE_VARIABLES[name].email) : TEMPLATE_VARIABLE_NAMES;

  function insert(variable: string): void {
    const token = `{{${variable}}}`;
    onChange({ ...input, value: input.type === "email" ? token : `${input.value}${token}` });
  }

  return (
    <div className="template-value">
      <Field label="Template" error={error} hint={input.type === "email" ? "One email variable, such as {{sender.email}}." : "Text with {{variables}} filled in when the action runs."}>
        {({ id, describedBy, invalid }) => (
          <Input
            id={id}
            className="mono"
            value={input.value}
            placeholder="{{sender.email}}"
            aria-describedby={describedBy}
            aria-invalid={invalid}
            onChange={(event) => { onChange({ ...input, value: event.target.value }); }}
          />
        )}
      </Field>
      <Select
        aria-label="Insert a variable"
        value=""
        onChange={(event) => {
          if (event.target.value !== "") insert(event.target.value);
        }}
      >
        <option value="">Insert a variable…</option>
        {variables.map((name) => (
          <option key={name} value={name}>
            {name} · {TEMPLATE_VARIABLES[name].description}
          </option>
        ))}
      </Select>
    </div>
  );
}
